/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    collection,
    query,
    where,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    orderBy,
    getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { EightDReport, EightDStatus, EightDMetrics } from '../types/eightd.types';

interface EightDContextType {
    reports: EightDReport[];
    loading: boolean;
    error: string | null;
    createReport: (report: Partial<EightDReport>) => Promise<string>;
    updateReport: (id: string, updates: Partial<EightDReport>) => Promise<void>;
    deleteReport: (id: string) => Promise<void>;
    getReportById: (id: string) => EightDReport | undefined;
    updateStatus: (id: string, status: EightDStatus, notes?: string) => Promise<void>;
    getMetrics: () => EightDMetrics;
}

const EightDContext = createContext<EightDContextType | undefined>(undefined);

export const useEightD = () => {
    const context = useContext(EightDContext);
    if (!context) {
        throw new Error('useEightD must be used within EightDProvider');
    }
    return context;
};

export const EightDProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [reports, setReports] = useState<EightDReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Real-time listener for 8D reports
    useEffect(() => {
        if (!user?.organizationId) {
            setReports([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const q = query(
            collection(db, 'eight_d_reports'),
            where('organizationId', '==', user.organizationId),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const reportsData = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        createdAt: data.createdAt?.toDate() || new Date(),
                        updatedAt: data.updatedAt?.toDate() || new Date(),
                        targetCompletionDate: data.targetCompletionDate?.toDate(),
                        actualCompletionDate: data.actualCompletionDate?.toDate(),
                        statusHistory: data.statusHistory?.map((h: any) => ({
                            ...h,
                            changedAt: h.changedAt?.toDate() || new Date(),
                        })) || [],
                    } as EightDReport;
                });
                setReports(reportsData);
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error('Error fetching 8D reports:', err);
                setError(err.message);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user?.organizationId]);

    // Generate next 8D number
    const generateEightDNumber = async (): Promise<string> => {
        const year = new Date().getFullYear();
        const prefix = `8D-${year}-`;

        const q = query(
            collection(db, 'eight_d_reports'),
            where('organizationId', '==', user?.organizationId),
            where('eightDNumber', '>=', prefix),
            where('eightDNumber', '<', `8D-${year + 1}-`)
        );

        const snapshot = await getDocs(q);
        const numbers = snapshot.docs
            .map(doc => doc.data().eightDNumber as string)
            .map(num => parseInt(num.split('-')[2]))
            .filter(num => !isNaN(num));

        const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
        return `${prefix}${String(nextNumber).padStart(4, '0')}`;
    };

    const createReport = async (report: Partial<EightDReport>): Promise<string> => {
        if (!user) throw new Error('User must be authenticated');

        const eightDNumber = await generateEightDNumber();

        const newReport = {
            ...report,
            eightDNumber,
            organizationId: user.organizationId || '',
            createdBy: user.uid,
            createdByName: user.displayName || user.email || 'Unknown',
            createdAt: Timestamp.fromDate(new Date()),
            updatedAt: Timestamp.fromDate(new Date()),
            status: report.status || 'Draft',
            statusHistory: [
                {
                    status: report.status || 'Draft',
                    changedBy: user.uid,
                    changedByName: user.displayName || user.email || 'Unknown',
                    changedAt: Timestamp.fromDate(new Date()),
                    notes: 'Report created',
                }
            ],
            completionStatus: report.completionStatus || {
                d0: false,
                d1: false,
                d2: false,
                d3: false,
                d4: false,
                d5: false,
                d6: false,
                d7: false,
                d8: false,
            },
            attachments: report.attachments || [],
            comments: report.comments || [],
            tags: report.tags || [],
        };

        const docRef = await addDoc(collection(db, 'eight_d_reports'), newReport);
        return docRef.id;
    };

    const updateReport = async (id: string, updates: Partial<EightDReport>): Promise<void> => {
        const docRef = doc(db, 'eight_d_reports', id);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: Timestamp.fromDate(new Date()),
        });
    };

    const deleteReport = async (id: string): Promise<void> => {
        const docRef = doc(db, 'eight_d_reports', id);
        await deleteDoc(docRef);
    };

    const getReportById = (id: string): EightDReport | undefined => {
        return reports.find(report => report.id === id);
    };

    const updateStatus = async (id: string, status: EightDStatus, notes?: string): Promise<void> => {
        if (!user) throw new Error('User must be authenticated');

        const report = getReportById(id);
        if (!report) throw new Error('Report not found');

        const newHistoryEntry = {
            status,
            changedBy: user.uid,
            changedByName: user.displayName || user.email || 'Unknown',
            changedAt: Timestamp.fromDate(new Date()),
            notes,
        };

        const updates: any = {
            status,
            statusHistory: [...report.statusHistory, newHistoryEntry],
            updatedAt: Timestamp.fromDate(new Date()),
        };

        // If closing, set completion date
        if (status === 'Closed' && !report.actualCompletionDate) {
            updates.actualCompletionDate = Timestamp.fromDate(new Date());
        }

        await updateReport(id, updates);
    };

    const getMetrics = (): EightDMetrics => {
        const total = reports.length;
        const active = reports.filter(r => r.status !== 'Closed').length;
        const completed = reports.filter(r => r.status === 'Closed').length;

        // Calculate average time to complete (in days)
        const completedReports = reports.filter(r => r.actualCompletionDate);
        const averageTimeToComplete = completedReports.length > 0
            ? Math.round(
                completedReports.reduce((sum, report) => {
                    const start = report.createdAt.getTime();
                    const end = report.actualCompletionDate!.getTime();
                    const days = (end - start) / (1000 * 60 * 60 * 24);
                    return sum + days;
                }, 0) / completedReports.length
            )
            : 0;

        // Count by status
        const byStatus: Record<EightDStatus, number> = {
            'Draft': 0,
            'In Progress': 0,
            'Review': 0,
            'Approved': 0,
            'Implemented': 0,
            'Closed': 0,
        };

        reports.forEach(report => {
            byStatus[report.status] = (byStatus[report.status] || 0) + 1;
        });

        const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

        return {
            total,
            active,
            completed,
            averageTimeToComplete,
            byStatus,
            completionRate,
        };
    };

    const value: EightDContextType = {
        reports,
        loading,
        error,
        createReport,
        updateReport,
        deleteReport,
        getReportById,
        updateStatus,
        getMetrics,
    };

    return (
        <EightDContext.Provider value={value}>
            {children}
        </EightDContext.Provider>
    );
};
